<template>
    <div class="card p-2" style="min-height: 200px">
        <div class="bg-blue-light p-1">
            <TitleButton
                btnTitle="Add New"
                :showBtn="false"
                :showAddNew="true"
                title="Money Receipt List"
                @onClickAddNewButton="onClickAddNewButton"
            />
        </div>

        <div class="my-2 px-2">
            <DateQuerySetter btnTitle="Go" @onClickGo="getMoneyReceiptList"/>
        </div>

        <div class="table-responsive px-2">
            <table class="table">
                <thead>
                    <tr>
                        <th>Voucher Date</th>
                        <th>Party Name</th>
                        <th>Txn Ref No</th>
                        <th class="text-end">Receipt Amount</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in receiptList" :key="i">
                        <td class="fw-bold">{{ item.receipt_date }}</td>
                        <td>{{ item.party }}</td>
                        <td>{{ item.voucher_no }}</td>
                        <td class="text-end">{{ commaFormat(item.total_paid_amount) }}</td>
                        <td>
                            <button class="btn btn-secondary btn-sm" @click="goToPrint(item.id)">
                                <i class="fas fa-print"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!receiptList.length">
                        <td colspan="5" class="text-center">No data</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="px-2">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import Pagination from '@/components/atom/Pagination';
import Loader from '@/components/atom/LoaderComponent';
import { inject, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import handleMoneyReceipt from '@/services/moneyReceipt';
import handlePurchase from '@/services/modules/purchase';
import handleCompany from '@/services/modules/company';
import figureFormatter from '@/services/utils/figureFormatter';
import moneyReceiptHelper from '@/services/utils/pdf/moneyReceiptHelper';

const props = defineProps({
    patientDetails: {
        type: Object,
        default: () => ({}),
    },
});

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { commaFormat } = figureFormatter();
const { fetchMoneyReceiptList } = handleMoneyReceipt();
const { fetchMoneyReceipt } = handlePurchase();
const { fetchCompanyInfo } = handleCompany();
const { generatePdf } = moneyReceiptHelper();

const offset = ref(20);
const loading = ref(false);
const receiptList = ref([]);
const companyInfo = ref({});

const getRegisterId = () => {
    return $route.params.ipdRegisterId || $route.query.ipd_register_id;
};

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += `&ipd_register_id=${getRegisterId()}`;
    query += '&exclude_receipt_type=pharmacy_advance,pharmacy_settlement,ipd_pharmacy_invoice';
    if ($route.query.start) query += '&start_date=' + $route.query.start;
    if ($route.query.end) query += '&end_date=' + $route.query.end;
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const getMoneyReceiptList = async () => {
    loading.value = true;
    await fetchMoneyReceiptList(getQuery()).then(res => {
        if (!res.status) {
            receiptList.value = [];
            $store.commit('resetPagination');
            return;
        }
        receiptList.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const onClickAddNewButton = () => {
    const contactId = props.patientDetails.contact_profile_id;
    const query = Object.assign({}, $route.query, {
        ipd_register_id: getRegisterId(),
        back_url: encodeURIComponent($route.fullPath),
    });
    if (contactId) query.contactId = contactId;
    $router.push({
        name: 'sales-invoice-receipt',
        params: $route.params,
        query: query,
    });
};

const goToPrint = (id) => {
    const companyQuery = `?company_id=${$route.params.companyId}`;
    fetchMoneyReceipt(id, companyQuery).then(res => {
        if (!res.status) return;
        generatePdf(companyInfo.value, res.data);
    }).catch(() => {
        showError('Something went wrong');
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    getMoneyReceiptList();
};

const getCompanyInfo = () => {
    fetchCompanyInfo($route.params.companyId).then(res => {
        if (res.data) companyInfo.value = res.data;
    });
};

getMoneyReceiptList();
getCompanyInfo();
</script>
